import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  userType: 'free' | 'executive' | 'admin';
  referralCode: string;
  rewardBalance: number;
  profile?: {
    phone: string;
    dateOfBirth?: string;
    gender?: string;
    address: {
      street: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
    } | string;
  };
  bankDetails?: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    accountHolderName: string;
    accountType: string;
    isVerified: boolean;
    status?: string;
  };
  kycDetails?: {
    panNumber: string;
    isVerified: boolean;
    status?: string;
    documents?: Array<{
      type: string;
      filename: string;
      originalName: string;
      mimeType: string;
      size: number;
      uploadDate: string;
      isVerified: boolean;
      verifiedAt?: string;
      rejectionReason?: string;
    }>;
  };
  profileLocked?: boolean;
  profileLockedAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  updateUser: (userData: User) => void;
  refreshUser: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (name: string, email: string, password: string, referralCode?: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token on app start
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        // Try actual API verification first
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`);
          if (response.data.success) {
            setUser(response.data.user);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.log('API token verification failed, checking for demo tokens...');
        }

        // Handle demo/mock tokens as fallback
        if (token === 'mock-admin-token-12345') {
          setUser({
            id: 'demo-admin-1',
            name: 'Demo Admin User',
            email: 'demo@networq.com',
            role: 'admin',
            userType: 'admin',
            referralCode: 'DEMO001',
            rewardBalance: 0,
          });
          setIsLoading(false);
          return;
        }
        
        if (token === 'mock-user-token-12345') {
          setUser({
            id: 'demo-user-1',
            name: 'Demo Test User',
            email: 'demouser@networq.com',
            role: 'user',
            userType: 'executive',
            referralCode: 'DEMOUSER',
            rewardBalance: 125.50,
          });
          setIsLoading(false);
          return;
        }

        // If neither API nor demo tokens work, clear auth
        console.error('Token verification failed');
        localStorage.removeItem('token');
        setToken(null);
      }
      setIsLoading(false);
    };

    verifyToken();
  }, [token, API_BASE_URL]);

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true);
      // Try actual API call first for all credentials
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/login`, {
          email,
          password
        });

        if (response.data.success) {
          const { token: newToken, user: userData } = response.data;
          localStorage.setItem('token', newToken);
          setToken(newToken);
          setUser(userData);
          setIsLoading(false);
          return { success: true, message: 'Login successful - connected to database' };
        } else {
          setIsLoading(false);
          return { success: false, message: response.data.message };
        }
      } catch (apiError: any) {
        console.log('API login failed, checking for demo credentials...', apiError.message);
        
        // Only fall back to mock authentication for demo purposes if API is unavailable
        if (email === 'demo@networqpay.com' && password === 'demo123') {
          const mockAdminUser: User = {
            id: 'demo-admin-1',
            name: 'Demo Admin User',
            email: 'demo@networq.com',
            role: 'admin',
            userType: 'admin',
            referralCode: 'DEMO001',
            rewardBalance: 0,
          };
          const mockToken = 'mock-admin-token-12345';
          
          localStorage.setItem('token', mockToken);
          setToken(mockToken);
          setUser(mockAdminUser);
          setIsLoading(false);
          return { success: true, message: 'Login successful - demo mode (no database connection)' };
        }
        
        if (email === 'demouser@networqpay.com' && password === 'demo123') {
          const mockUser: User = {
            id: 'demo-user-1',
            name: 'Demo Test User',
            email: 'demouser@networq.com',
            role: 'user',
            userType: 'executive',
            referralCode: 'DEMOUSER',
            rewardBalance: 125.50,
          };
          const mockToken = 'mock-user-token-12345';
          
          localStorage.setItem('token', mockToken);
          setToken(mockToken);
          setUser(mockUser);
          setIsLoading(false);
          return { success: true, message: 'Login successful - demo mode (no database connection)' };
        }

        // If API fails and not demo credentials, return error
        setIsLoading(false);
        return { success: false, message: apiError.response?.data?.message || 'Invalid credentials or server unavailable' };
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      setIsLoading(false);
      return { success: false, message };
    }
  };

  const register = async (
    name: string, 
    email: string, 
    password: string, 
    referralCode?: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        name,
        email,
        password,
        referralCode
      });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        return { success: true, message: 'Registration successful' };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, message };
    }
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify`);
      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [token, API_BASE_URL]);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    updateUser,
    refreshUser,
    login,
    register,
    logout,
    isLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};